import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2, Download, FileText, RotateCcw, Play, Percent, Clock,
  ChevronRight, CalendarDays,
} from "lucide-react";
import api from "@/lib/api";
import { formatUSD } from "@/lib/utils";
import DateRangePopover from "@/components/DateRangePopover";

// LocalStorage key — remembers the user's last-picked date range across
// tab switches and page reloads.
const DATE_RANGE_STORAGE_KEY = "globetopup.mcp.dateRange";

function loadStoredRange() {
  try {
    const raw = localStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.from && parsed?.to) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}
function saveStoredRange(from, to) {
  try {
    localStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify({ from, to }));
  } catch {
    /* ignore — localStorage may be blocked */
  }
}

/**
 * MASTER_ADMIN-only view: weekly payout ledger with manual trigger and
 * reversal controls. This is the network-wide operational cockpit — every
 * Master Agent's Monday-2AM payout appears here.
 *
 * The per-agent breakdown view (for MASTER_AGENT and drill-down) lives at
 * /master-agent-commissions.
 */
export default function MasterCommissionsPayouts() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("current");
  const [summary, setSummary] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const stored = loadStoredRange();
    return {
      master_agent_id: "",
      status: "",
      week_from: stored?.from || "",
      week_to: stored?.to || "",
    };
  });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSummary = () => {
    api.get("/master-commissions/summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  };

  const loadWeekly = () => {
    const p = {};
    if (filters.master_agent_id) p.master_agent_id = filters.master_agent_id;
    if (filters.status) p.status = filters.status;
    if (filters.week_from) p.week_from = filters.week_from;
    if (filters.week_to) p.week_to = filters.week_to;
    return api
      .get("/master-commissions/weekly", { params: p })
      .then((r) => setWeekly(r.data.items || []));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadSummary(),
      loadWeekly(),
      api.get("/master-agents").then((r) => setMasters(r.data || [])).catch(() => setMasters([])),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadWeekly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.master_agent_id, filters.status, filters.week_from, filters.week_to]);

  const triggerNow = async () => {
    if (!summary || !summary.items?.length) {
      toast.info("No pending accruals to pay out.");
      return;
    }
    if (!window.confirm(`Pay out ${summary.items.length} master agent(s) for the current week?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/master-commissions/weekly/trigger", {
        week_start: summary.week_start,
        week_end: summary.week_end,
      });
      toast.success(`Paid ${r.data.count} master agent(s)`);
      loadSummary();
      loadWeekly();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trigger failed");
    } finally {
      setBusy(false);
    }
  };

  const reversePayout = async (row) => {
    if (!window.confirm(
      `Reverse ${formatUSD(row.total_amount_usd)} for ${row.master_agent_username}? ` +
      `Their wallet will be debited and may go negative.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/master-commissions/weekly/${row.id}/reverse`);
      toast.success("Payout reversed");
      loadWeekly();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reverse failed");
    } finally {
      setBusy(false);
    }
  };

  const triggerHistorical = async (row) => {
    if (!window.confirm(`Re-trigger payout for ${row.master_agent_username} for week ${row.week_start.slice(0, 10)}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/master-commissions/weekly/trigger", {
        week_start: row.week_start,
        week_end: row.week_end,
        master_agent_id: row.master_agent_id,
      });
      if (r.data.count > 0) toast.success("Payout created");
      else toast.info("Nothing to pay (all accruals already paid)");
      loadWeekly();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trigger failed");
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (row) => {
    setDetail({ ...row, accruals: null });
    try {
      const r = await api.get(`/master-commissions/weekly/${row.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Failed to load details");
    }
  };

  const totals = useMemo(() => {
    return {
      pending: (summary?.items || []).reduce((s, r) => s + Number(r.total_amount_usd || 0), 0),
      paid_total: weekly.filter((r) => r.status === "PAID").reduce((s, r) => s + Number(r.total_amount_usd || 0), 0),
      reversed_total: weekly.filter((r) => r.status === "REVERSED").reduce((s, r) => s + Number(r.total_amount_usd || 0), 0),
    };
  }, [summary, weekly]);

  const doExport = async (kind) => {
    try {
      const r = await api.get(`/master-commissions/export.${kind}`, {
        params: {
          master_agent_id: filters.master_agent_id || undefined,
          status: filters.status || undefined,
          week_from: filters.week_from || undefined,
          week_to: filters.week_to || undefined,
        },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: kind === "csv" ? "text/csv" : "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `master-commissions.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-slate-500" data-testid="mcp-loading">
        <Loader2 className="w-6 h-6 animate-spin inline" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="master-commissions-payouts-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("mcp.eyebrow", "Weekly payouts")}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("mcp.title", "Master Commission Payouts")}
        </h1>
        <p className="text-slate-500 mt-1 max-w-2xl">
          {t(
            "mcp.subtitle",
            "Weekly commission payouts to each Master Agent. Automatic every Monday 02:00 platform time. Reverse or re-trigger for special cases."
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          testid="mcp-pending"
          label={t("mcp.pending_this_week", "Pending this week")}
          value={formatUSD(totals.pending)}
          icon={<Clock className="w-5 h-5" />}
          tone="amber"
          sub={summary?.next_payout_at ? `Next payout ${new Date(summary.next_payout_at).toLocaleString()}` : "—"}
        />
        <StatCard
          testid="mcp-paid"
          label={t("mcp.paid_total", "Paid (in view)")}
          value={formatUSD(totals.paid_total)}
          icon={<Percent className="w-5 h-5" />}
          tone="emerald"
          sub={`${weekly.filter((r) => r.status === "PAID").length} payouts`}
        />
        <StatCard
          testid="mcp-reversed"
          label={t("mcp.reversed_total", "Reversed (in view)")}
          value={formatUSD(totals.reversed_total)}
          icon={<RotateCcw className="w-5 h-5" />}
          tone="rose"
          sub={`${weekly.filter((r) => r.status === "REVERSED").length} reversals`}
        />
      </div>

      <div className="border-b border-slate-200 flex gap-4">
        <TabBtn active={tab === "current"} onClick={() => setTab("current")} testid="mcp-tab-current">Current week</TabBtn>
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="mcp-tab-history">Weekly history</TabBtn>
      </div>

      {tab === "current" ? (
        <CurrentWeekPanel summary={summary} onTrigger={triggerNow} busy={busy} />
      ) : (
        <HistoryPanel
          weekly={weekly}
          masters={masters}
          filters={filters}
          setFilters={setFilters}
          onReverse={reversePayout}
          onRetrigger={triggerHistorical}
          onDetail={openDetail}
          onExport={doExport}
          busy={busy}
        />
      )}

      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function StatCard({ testid, label, value, sub, icon, tone = "blue" }) {
  const toneCls = {
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    blue: "border-[#0F62FE]/20 bg-[#0F62FE]/5 text-[#0F62FE]",
  }[tone];
  return (
    <div data-testid={testid} className={`rounded-lg border p-4 ${toneCls}`}>
      <div className="flex items-center justify-between opacity-80">
        <div className="text-[10px] font-bold uppercase tracking-widest">{label}</div>
        {icon}
      </div>
      <div className="text-3xl font-bold mt-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={"px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors " + (active ? "border-[#0F62FE] text-[#0F62FE]" : "border-transparent text-slate-500 hover:text-slate-900")}
    >
      {children}
    </button>
  );
}

function CurrentWeekPanel({ summary, onTrigger, busy }) {
  const items = summary?.items || [];
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            Week of {summary?.week_start ? summary.week_start.slice(0, 10) : "—"}
          </div>
          <div className="text-xs text-slate-500">
            Timezone: {summary?.tz_name || "UTC"} · Next auto-payout: {summary?.next_payout_at ? new Date(summary.next_payout_at).toLocaleString() : "—"}
          </div>
        </div>
        <button
          data-testid="mcp-trigger-now"
          onClick={onTrigger}
          disabled={busy || items.length === 0}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Pay out now
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="mcp-current-table">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
              <th className="px-5 py-3 text-left">Master Agent</th>
              <th className="px-5 py-3 text-right">Accruals</th>
              <th className="px-5 py-3 text-right">Amount (USD)</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-500">No accruals yet for this week.</td></tr>
            ) : items.map((r) => (
              <tr key={r.master_agent_id} data-testid={`mcp-cw-row-${r.master_agent_id}`} className="border-b border-slate-100">
                <td className="px-5 py-3 font-medium text-slate-900">
                  {r.master_agent_name || r.master_agent_username || "—"}
                  <span className="ml-2 text-xs text-slate-500">@{r.master_agent_username}</span>
                </td>
                <td className="px-5 py-3 text-right text-slate-600">{r.accrual_count}</td>
                <td className="px-5 py-3 text-right font-bold text-slate-900">{formatUSD(r.total_amount_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HistoryPanel({ weekly, masters, filters, setFilters, onReverse, onRetrigger, onDetail, onExport, busy }) {
  const onRangeChange = (f, tt) => {
    setFilters((prev) => ({ ...prev, week_from: f, week_to: tt }));
    saveStoredRange(f, tt);
  };
  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Master Agent</label>
          <select
            data-testid="mcp-filter-master"
            value={filters.master_agent_id}
            onChange={(e) => setFilters((f) => ({ ...f, master_agent_id: e.target.value }))}
            className="h-11 w-full rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
          >
            <option value="">All</option>
            {masters.map((m) => (<option key={m.id} value={m.id}>{m.username}</option>))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Status</label>
          <select
            data-testid="mcp-filter-status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="h-11 w-full rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
          >
            <option value="">All</option>
            <option value="PAID">Paid</option>
            <option value="PENDING">Pending</option>
            <option value="REVERSED">Reversed</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Date range</label>
          <DateRangePopover
            testidPrefix="mcp-daterange"
            from={filters.week_from || ""}
            to={filters.week_to || ""}
            onChange={onRangeChange}
          />
        </div>
        <div className="flex items-end gap-2">
          <button data-testid="mcp-export-csv" onClick={() => onExport("csv")} className="inline-flex items-center gap-1.5 h-11 px-3 rounded-md border border-slate-300 bg-white text-sm hover:bg-slate-50">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button data-testid="mcp-export-pdf" onClick={() => onExport("pdf")} className="inline-flex items-center gap-1.5 h-11 px-3 rounded-md border border-slate-300 bg-white text-sm hover:bg-slate-50">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mcp-history-table">
            <thead>
              <tr className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 text-left">Week</th>
                <th className="px-5 py-3 text-left">Master Agent</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Accruals</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Paid at</th>
                <th className="px-5 py-3 text-right w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {weekly.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No weekly payouts match your filters yet.</td></tr>
              ) : weekly.map((r) => (
                <tr key={r.id} data-testid={`mcp-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <div className="text-slate-900 font-medium flex items-center gap-1">
                      <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                      {r.week_start.slice(0, 10)}
                    </div>
                    <div className="text-xs text-slate-500">to {r.week_end.slice(0, 10)}</div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{r.master_agent_name || "—"}</div>
                    <div className="text-xs text-slate-500">@{r.master_agent_username}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-slate-900">{formatUSD(r.total_amount_usd)}</td>
                  <td className="px-5 py-3 text-right text-slate-600">{r.accrual_count}</td>
                  <td className="px-5 py-3"><StatusPill status={r.status} /></td>
                  <td className="px-5 py-3 text-slate-600 text-xs">{r.paid_at ? new Date(r.paid_at).toLocaleString() : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button data-testid={`mcp-detail-${r.id}`} onClick={() => onDetail(r)} className="inline-flex items-center gap-1 h-8 px-3 rounded border border-slate-300 bg-white text-xs hover:bg-slate-50">
                        Details <ChevronRight className="w-3 h-3" />
                      </button>
                      {r.status === "PAID" && (
                        <button data-testid={`mcp-reverse-${r.id}`} onClick={() => onReverse(r)} disabled={busy} className="inline-flex items-center gap-1 h-8 px-3 rounded border border-rose-300 bg-rose-50 text-rose-700 text-xs hover:bg-rose-100 disabled:opacity-50">
                          <RotateCcw className="w-3 h-3" /> Reverse
                        </button>
                      )}
                      {r.status === "REVERSED" && (
                        <button data-testid={`mcp-retrigger-${r.id}`} onClick={() => onRetrigger(r)} disabled={busy} className="inline-flex items-center gap-1 h-8 px-3 rounded border border-[#0F62FE] bg-[#0F62FE]/5 text-[#0F62FE] text-xs hover:bg-[#0F62FE]/10 disabled:opacity-50">
                          <Play className="w-3 h-3" /> Re-trigger
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function StatusPill({ status }) {
  const cls = {
    PAID: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
    REVERSED: "bg-rose-50 text-rose-700 border-rose-200",
  }[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span data-testid={`mcp-status-${status}`} className={`inline-flex px-2 py-0.5 rounded-full font-bold text-[10px] uppercase tracking-widest border ${cls}`}>
      {status}
    </span>
  );
}

function DetailModal({ detail, onClose }) {
  return (
    <div data-testid="mcp-detail-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-bold tracking-widest text-slate-500">Weekly payout detail</div>
            <div className="text-lg font-bold text-slate-900">{detail.master_agent_name} · {detail.week_start?.slice(0, 10)}</div>
          </div>
          <button data-testid="mcp-detail-close" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Total</div>
              <div className="text-xl font-bold text-slate-900">{formatUSD(detail.total_amount_usd)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Accruals</div>
              <div className="text-xl font-bold text-slate-900">{detail.accrual_count}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">Status</div>
              <div><StatusPill status={detail.status} /></div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500 mb-2">Individual accruals</div>
            {detail.accruals === null ? (
              <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
            ) : (detail.accruals || []).length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">No accruals in this payout.</div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-md">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Agent</th>
                      <th className="px-3 py-2 text-left">Operator</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-right">Admin %</th>
                      <th className="px-3 py-2 text-right">Agent %</th>
                      <th className="px-3 py-2 text-right">Accrual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.accruals.map((a) => (
                      <tr key={a.id} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 text-slate-600">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-1.5 text-slate-800">{a.agent_username || "—"}</td>
                        <td className="px-3 py-1.5 text-slate-800">{a.operator_name || a.operator_id}</td>
                        <td className="px-3 py-1.5 text-right text-slate-800">{formatUSD(a.topup_amount_usd)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-800">{Number(a.admin_pct).toFixed(2)}%</td>
                        <td className="px-3 py-1.5 text-right text-slate-800">{Number(a.agent_pct).toFixed(2)}%</td>
                        <td className="px-3 py-1.5 text-right font-bold text-emerald-700">{formatUSD(a.accrual_amount_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
